/**
 * Variant Launch: real WebXR on iPhone, through an App Clip. (PoC)
 *
 * iOS Safari exposes no WebXR at all, so the WebXR path never runs there on
 * its own. The Variant Launch SDK works around that with an App Clip: the
 * user hops through a "Launch Card", the App Clip opens this page again
 * inside its own WebXR-capable browser view, and from there the ordinary
 * WebXR path runs against ARKit — the same `local` + `hit-test` +
 * `dom-overlay` features it already uses on Android Chrome.
 *
 * The SDK is loaded here, on demand, rather than as the script tag in
 * `index.html` the quick-start shows: injecting it after our event listener
 * is registered means `vlaunch-initialized` cannot fire before anyone is
 * listening, and skipping `redirect=true` keeps iPhone visitors who never
 * touch Live AR out of the App Clip — the redirect happens only from the
 * Live AR button, through `getLaunchUrl`.
 */

import { isWebXrArSupported } from "./arLiveWebXr";

/** What the SDK announces once it has sized up the browser. */
export interface VariantLaunchState {
  /**
   * True on an iPhone outside the Launch viewer: WebXR exists, but only on
   * the far side of the App Clip hop.
   */
  launchRequired: boolean;
  webXRStatus: "unsupported" | "launch-required" | "supported";
  /** Launch-card URL that reopens the current page inside the viewer. */
  launchUrl: string;
}

declare global {
  interface Window {
    VLaunch?: { getLaunchUrl: (url: string) => string };
  }
  interface WindowEventMap {
    "vlaunch-initialized": CustomEvent<VariantLaunchState>;
  }
}

const SDK_URL = "https://launchar.app/sdk/v1";

/**
 * This project's Variant Launch key (free Developer plan). It is a
 * client-side key: every visitor's browser sees it in the script URL, and
 * what it may serve is bounded by the authorised domains on the Variant
 * dashboard — so it is committed rather than threaded through the docker
 * build, and `VITE_VARIANT_LAUNCH_KEY` overrides it where needed.
 */
const DEFAULT_SDK_KEY = "I2zmUBGkAfczLcZHUOCsmhSEn8xuaLQX";

/**
 * The SDK announces itself with an event; a script that loads but never
 * fires it would otherwise leave the Live AR button waiting forever.
 */
const INIT_TIMEOUT_MS = 10000;

/**
 * The query parameter that survives the App Clip round trip: the page
 * reloads inside the Launch viewer, and this is how it knows to go straight
 * back into Live AR instead of landing on the builder.
 */
export const LIVE_AR_AUTO_OPEN_PARAM = "live-ar";

/**
 * The query parameter carrying the session hand-off ticket. The App Clip's
 * browser has no Safari cookies, so this is the only channel that can prove
 * to the server that the returning viewer is still the session's owner.
 */
export const LIVE_AR_TICKET_PARAM = "ar-ticket";

let statePromise: Promise<VariantLaunchState | null> | null = null;

/**
 * Load the SDK once and report whether Live AR needs the App Clip hop.
 *
 * Resolves null whenever the hop is irrelevant: no key configured, or the
 * browser already has immersive-ar of its own (Android Chrome, and the
 * Launch viewer itself) — in both cases without loading any script. A
 * failed or silent SDK load also resolves null, so the button falls back to
 * the 8th Wall path rather than hanging.
 */
export function loadVariantLaunch(
  key: string | undefined = import.meta.env.VITE_VARIANT_LAUNCH_KEY ?? DEFAULT_SDK_KEY
): Promise<VariantLaunchState | null> {
  if (!statePromise) {
    statePromise = (async () => {
      if (!key) {
        return null;
      }
      if (await isWebXrArSupported()) {
        return null;
      }
      return await new Promise<VariantLaunchState | null>((resolve) => {
        const timeout = window.setTimeout(() => {
          console.warn("Variant Launch did not initialise; keeping the 8th Wall path.");
          resolve(null);
        }, INIT_TIMEOUT_MS);
        window.addEventListener(
          "vlaunch-initialized",
          (event) => {
            window.clearTimeout(timeout);
            resolve(event.detail);
          },
          { once: true }
        );
        const script = document.createElement("script");
        script.src = `${SDK_URL}?key=${encodeURIComponent(key)}`;
        script.async = true;
        script.onerror = () => {
          window.clearTimeout(timeout);
          console.warn("The Variant Launch SDK could not be loaded; keeping the 8th Wall path.");
          resolve(null);
        };
        document.head.appendChild(script);
      });
    })();
  }
  return statePromise;
}

/**
 * The URL to send the user through the App Clip and back to `returnUrl`.
 *
 * `getLaunchUrl` targets an exact return URL — ours carries the auto-open
 * parameter — while the state's own `launchUrl` merely reopens the page as
 * it was at SDK init, so it is only the fallback.
 */
export function variantLaunchUrl(state: VariantLaunchState, returnUrl: string): string {
  return window.VLaunch?.getLaunchUrl(returnUrl) ?? state.launchUrl;
}
